// Dashboard — top-level studio pulse. Real aggregated stats from the API.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, adminApi, type AdminDashboardStats, DashboardData } from '../lib/api';
import { PageHeader } from '../components/Page';
import { T } from '../theme';

function AdminPulse() {
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    adminApi
      .dashboardStats()
      .then(setStats)
      .catch((e) => setError(String(e)));
  };

  useEffect(load, []);

  if (error) {
    return (
      <div style={{ padding: 16, background: T.bgCard, borderRadius: 8, color: T.error, fontSize: 13, marginBottom: 24 }}>
        Admin pulse failed: {error}
      </div>
    );
  }
  if (!stats) return null;

  const cards = [
    { label: 'Pending brand reviews', value: stats.counts.pending_brands, accent: stats.counts.pending_brands > 0 },
    { label: 'Draft campaigns', value: stats.counts.draft_campaigns, accent: stats.counts.draft_campaigns > 0 },
    { label: 'Active campaigns', value: stats.counts.active_campaigns, accent: false },
    { label: 'Clips awaiting brand', value: stats.counts.submitted_clips_pending_brand, accent: false },
  ];

  return (
    <section style={{ marginBottom: 32 }}>
      <div style={{ fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase', color: T.inkSoft, marginBottom: 12 }}>
        Studio pulse
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {cards.map((c) => (
          <div
            key={c.label}
            style={{
              padding: 16,
              background: T.bgCard,
              border: `1px solid ${T.rule}`,
              borderRadius: 8,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {c.accent && (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: T.accent }} />
            )}
            <div style={{ fontSize: 10, letterSpacing: '0.25em', textTransform: 'uppercase', color: T.inkMute, marginBottom: 6 }}>
              {c.label}
            </div>
            <div style={{ fontFamily: T.font.display, fontSize: 28, color: c.accent ? T.accent : T.ink, fontWeight: 400 }}>
              {c.value}
            </div>
          </div>
        ))}
      </div>

      {stats.counts.pending_brands > 0 && (
        <div style={{ padding: 14, background: T.warningSoft, border: `1px solid ${T.warning}`, borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
          <strong style={{ color: T.warning }}>{stats.counts.pending_brands}</strong>
          <span style={{ color: T.inkSoft }}> brand{stats.counts.pending_brands === 1 ? '' : 's'} awaiting approval. </span>
          <Link to="/operator/brands" style={{ color: T.accent, textDecoration: 'underline' }}>
            Review queue →
          </Link>
        </div>
      )}

      {stats.drafts.length > 0 && (
        <div style={{ background: T.bgCard, border: `1px solid ${T.rule}`, borderRadius: 8, padding: 16 }}>
          <div style={{ fontSize: 11, letterSpacing: '0.25em', textTransform: 'uppercase', color: T.inkSoft, marginBottom: 12 }}>
            Drafts to review
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {stats.drafts.map((d) => (
              <div
                key={d.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 12px',
                  background: T.bg,
                  borderRadius: 6,
                  fontSize: 13,
                  gap: 12,
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <Link to={`/operator/campaigns/${d.id}`} style={{ color: T.ink, fontWeight: 500 }}>
                    {d.title}
                  </Link>
                  <span style={{ color: T.inkMute, marginLeft: 12 }}>
                    {d.brand_name} · ${((d.budget_total || 0) / 100).toLocaleString()}
                  </span>
                </div>
                <Link
                  to={`/operator/campaigns/${d.id}`}
                  style={{
                    padding: '6px 12px',
                    fontSize: 11,
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    background: T.accent,
                    color: T.bg,
                    border: 'none',
                    borderRadius: 4,
                    fontWeight: 600,
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Review
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function formatMoney(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function formatNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString('en-US');
}

function timeSince(ts: string): string {
  const now = Date.now();
  const t = new Date(ts.replace(' ', 'T') + (ts.includes('Z') ? '' : 'Z')).getTime();
  if (Number.isNaN(t)) return ts;
  const sec = Math.max(0, Math.floor((now - t) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

function statusColor(status: string): string {
  switch (status) {
    case 'active':
    case 'verified': return T.live;
    case 'draft':
    case 'submitted':
    case 'pending':
    case 'claimed': return T.warning;
    case 'paused': return T.inkMute;
    case 'completed': return T.inkSoft;
    case 'rejected':
    case 'cancelled': return T.error;
    default: return T.inkSoft;
  }
}

export default function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.performance
      .dashboard()
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e) => {
        if (!cancelled) setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <PageHeader
        title="Today"
        subtitle="The studio's pulse — active campaigns, recent activity, what needs attention."
      />

      <AdminPulse />

      {loading && (
        <div style={dashStyles.empty}>
          <span style={{ color: T.inkMute, fontStyle: 'normal' }}>Loading dashboard…</span>
        </div>
      )}

      {error && !loading && (
        <div style={dashStyles.error}>
          <div style={{ color: T.error, fontFamily: T.font.mono, fontStyle: 'normal', marginBottom: 8 }}>
            Failed to load dashboard
          </div>
          <div style={{ color: T.inkSoft, fontSize: 13, fontFamily: T.font.mono }}>
            {error.message}
          </div>
        </div>
      )}

      {data && (
        <>
          {/* Stat row */}
          <div style={dashStyles.statRow}>
            <Stat label="Active campaigns"   value={String(data.campaigns.active)}        sub={`${data.campaigns.draft} drafting · ${data.campaigns.completed} completed`} />
            <Stat label="Verified views"     value={formatNum(data.clips.total_views)}    sub={`${data.clips.verified} verified clips`} />
            <Stat label="Pending payouts"    value={formatMoney(data.payouts.pending_cents)} sub={`${data.payouts.pending_count} clips awaiting`} />
            <Stat label="Active creators"    value={String(data.creators.active_30d)}    sub="claimed in last 30 days" />
          </div>

          {/* Pipeline stat row */}
          <div style={{ ...dashStyles.statRow, marginTop: 18 }}>
            <Stat label="Clip pipeline"   value={String(data.clips.total)}      sub={`${data.clips.claimed} claimed · ${data.clips.submitted} submitted · ${data.clips.verified} verified`} />
            <Stat label="Total budget"    value={formatMoney(data.spend.total_budget_cents)} sub="across active + completed campaigns" />
            <Stat label="Total spent"     value={formatMoney(data.spend.total_spent_cents)}
                  sub={`${data.spend.total_budget_cents ? Math.round((data.spend.total_spent_cents / data.spend.total_budget_cents) * 100) : 0}% utilized`} />
            <Stat label="Submissions"     value={String(data.clips.submitted)} sub="awaiting operator verification" />
          </div>

          {/* Two-column: top campaigns + recent activity */}
          <div style={dashStyles.twoCol}>
            {/* Top campaigns */}
            <div style={dashStyles.col}>
              <div style={dashStyles.colLabel}>Top campaigns by verified views</div>
              {data.top_campaigns.length === 0 ? (
                <div style={dashStyles.empty}>
                  <span style={{ color: T.inkMute, fontStyle: 'normal' }}>No verified views yet.</span>
                </div>
              ) : (
                <ul style={dashStyles.topList}>
                  {data.top_campaigns.map((c) => (
                    <li key={c.id} style={dashStyles.topItem}>
                      <Link to={`/operator/campaigns/${c.id}`} style={dashStyles.topLink}>
                        <div style={dashStyles.topItemHeader}>
                          <span style={dashStyles.topBrand}>{c.brand_name || '—'}</span>
                          <span style={{ color: statusColor(c.status), fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase' }}>
                            ● {c.status}
                          </span>
                        </div>
                        <div style={dashStyles.topTitle}>{c.title}</div>
                        {c.artist && <div style={dashStyles.topArtist}>{c.artist}</div>}
                        <div style={dashStyles.topMeta}>
                          <span>{formatNum(c.total_views)} verified views</span>
                          <span style={{ color: T.inkMute }}> · </span>
                          <span>{c.clip_count} clips</span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Recent activity */}
            <div style={dashStyles.col}>
              <div style={dashStyles.colLabel}>Recent activity</div>
              {data.activity.length === 0 ? (
                <div style={dashStyles.empty}>
                  <span style={{ color: T.inkMute, fontStyle: 'normal' }}>No recent activity.</span>
                </div>
              ) : (
                <ul style={dashStyles.activityList}>
                  {data.activity.map((a) => (
                    <li key={`${a.kind}-${a.id}`} style={dashStyles.activityItem}>
                      <div style={dashStyles.activityHeader}>
                        <span style={dashStyles.activityKind}>{a.kind}</span>
                        <span style={{ color: statusColor(a.status), fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase' }}>
                          {a.status}
                        </span>
                        <span style={dashStyles.activityTime}>{timeSince(a.ts)}</span>
                      </div>
                      <div style={dashStyles.activitySubject}>
                        {a.kind === 'campaign' ? (
                          <Link to={`/operator/campaigns/${a.id}`} style={dashStyles.activityLink}>{a.subject}</Link>
                        ) : (
                          <span>{a.subject}</span>
                        )}
                      </div>
                      {a.brand && <div style={dashStyles.activityBrand}>{a.brand}</div>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div style={dashStyles.stat}>
      <div style={dashStyles.statValue}>{value}</div>
      <div style={dashStyles.statLabel}>{label}</div>
      {sub && <div style={dashStyles.statSub}>{sub}</div>}
    </div>
  );
}

const dashStyles: Record<string, React.CSSProperties> = {
  empty: {
    padding: 64,
    textAlign: 'center' as const,
    border: `1px solid ${T.rule}`,
    fontSize: 14,
  },
  error: {
    padding: 32,
    border: `1px solid ${T.error}`,
    background: T.errorSoft,
  },
  statRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 18,
  },
  stat: {
    padding: '24px 20px',
    border: `1px solid ${T.rule}`,
  },
  statValue: {
    fontFamily: T.font.display,
    fontSize: 28,
    color: T.accent,
    letterSpacing: '0.02em',
    marginBottom: 8,
    lineHeight: 1,
  },
  statLabel: {
    fontSize: 10,
    letterSpacing: '0.18em',
    textTransform: 'uppercase' as const,
    color: T.inkSoft,
  },
  statSub: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 11,
    color: T.inkMute,
    marginTop: 6,
    lineHeight: 1.5,
  },
  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 28,
    marginTop: 36,
  },
  col: {},
  colLabel: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 11,
    letterSpacing: '0.22em',
    textTransform: 'uppercase' as const,
    color: T.accent,
    marginBottom: 14,
  },
  topList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  topItem: {
    border: `1px solid ${T.rule}`,
    marginBottom: 10,
    transition: 'border-color 0.2s',
  },
  topLink: {
    display: 'block',
    padding: '16px 20px',
    color: 'inherit',
    textDecoration: 'none',
  },
  topItemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: 6,
  },
  topBrand: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 12,
    color: T.inkSoft,
    letterSpacing: '0.04em',
  },
  topTitle: {
    fontFamily: T.font.display,
    fontSize: 17,
    color: T.ink,
    letterSpacing: '0.02em',
    marginBottom: 4,
    fontWeight: 400,
  },
  topArtist: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 12,
    color: T.accent,
    marginBottom: 8,
  },
  topMeta: {
    fontFamily: T.font.mono,
    fontSize: 11,
    color: T.inkSoft,
    letterSpacing: '0.02em',
  },
  activityList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  activityItem: {
    padding: '14px 0',
    borderBottom: `1px solid ${T.rule}`,
  },
  activityHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 4,
  },
  activityKind: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 10,
    letterSpacing: '0.16em',
    textTransform: 'uppercase' as const,
    color: T.accent,
  },
  activityTime: {
    marginLeft: 'auto',
    fontFamily: T.font.mono,
    fontSize: 10,
    color: T.inkMute,
  },
  activitySubject: {
    fontSize: 14,
    color: T.ink,
    fontFamily: T.font.body,
    marginBottom: 2,
  },
  activityLink: {
    color: T.ink,
    textDecoration: 'none',
    borderBottom: `1px solid transparent`,
    transition: 'border-color 0.2s',
  },
  activityBrand: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 11,
    color: T.inkSoft,
  },
};
